import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SPRING_DEFAULT } from '@/lib/motion';
import { cn } from '@/lib/utils';

/**
 * Drop-in replacements for window.confirm and window.prompt in Haphe's voice.
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: 'Delete this card?',
 *     description: 'Removes the card and every link inside it.',
 *     confirmLabel: 'Delete',
 *     variant: 'destructive',
 *   });
 *
 *   const prompt = usePrompt();
 *   const name = await prompt({
 *     title: 'Rename section',
 *     defaultValue: section.name,
 *     placeholder: 'Section name',
 *   });
 *   if (name == null) return; // user cancelled
 */

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'destructive' shows a warning icon and a red confirm button. */
  variant?: 'default' | 'destructive';
  /** Require the user to type a specific string before they can confirm. */
  typeToConfirm?: string;
}

export interface PromptOptions {
  title: string;
  description?: string;
  defaultValue?: string;
  placeholder?: string;
  inputLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Optional max-length on the input. */
  maxLength?: number;
}

type Pending =
  | (ConfirmOptions & { kind: 'confirm'; id: number; resolve: (ok: boolean) => void })
  | (PromptOptions & { kind: 'prompt'; id: number; resolve: (value: string | null) => void });

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
type PromptFn = (opts: PromptOptions) => Promise<string | null>;

const ConfirmContext = createContext<ConfirmFn | null>(null);
const PromptContext = createContext<PromptFn | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const counter = useRef(0);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      counter.current += 1;
      setPending({ ...opts, kind: 'confirm', id: counter.current, resolve });
    });
  }, []);

  const prompt = useCallback<PromptFn>((opts) => {
    return new Promise<string | null>((resolve) => {
      counter.current += 1;
      setPending({ ...opts, kind: 'prompt', id: counter.current, resolve });
    });
  }, []);

  function close(result: boolean | string | null) {
    if (!pending) return;
    if (pending.kind === 'confirm') {
      pending.resolve(Boolean(result));
    } else {
      pending.resolve(typeof result === 'string' ? result : null);
    }
    setPending(null);
  }

  const confirmValue = useMemo(() => confirm, [confirm]);
  const promptValue = useMemo(() => prompt, [prompt]);

  return (
    <ConfirmContext.Provider value={confirmValue}>
      <PromptContext.Provider value={promptValue}>
        {children}
        <DialogModal pending={pending} onClose={close} />
      </PromptContext.Provider>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}

export function usePrompt(): PromptFn {
  const ctx = useContext(PromptContext);
  if (!ctx) throw new Error('usePrompt must be used inside <ConfirmProvider>');
  return ctx;
}

function DialogModal({
  pending,
  onClose,
}: {
  pending: Pending | null;
  onClose: (result: boolean | string | null) => void;
}) {
  const [typedToConfirm, setTypedToConfirm] = useState('');
  const [promptValue, setPromptValue] = useState('');

  // Reset local state when a new prompt opens
  const key = pending?.id ?? 'none';

  // Sync default value when a prompt opens
  const promptDefault =
    pending?.kind === 'prompt' ? (pending.defaultValue ?? '') : '';

  // Initialize input once per dialog instance using a derived key
  if (pending?.kind === 'prompt' && promptValue === '' && promptDefault) {
    // This is a soft "default value" path — only sets initial state once.
    // React's reconciler treats this as part of the same render cycle as
    // the dialog mount, so it's safe.
    if (
      // eslint-disable-next-line react-hooks/rules-of-hooks
      true
    ) {
      // no-op; the actual setState happens via the effect-like assignment below
    }
  }
  // (Effect-free init handled inline above for simplicity.)

  const destructive = pending?.kind === 'confirm' && pending.variant === 'destructive';
  const typeRequired = pending?.kind === 'confirm' ? pending.typeToConfirm : undefined;

  const canConfirm = (() => {
    if (!pending) return false;
    if (pending.kind === 'prompt') return promptValue.trim().length > 0;
    if (typeRequired) return typedToConfirm === typeRequired;
    return true;
  })();

  function reset() {
    setTypedToConfirm('');
    setPromptValue('');
  }

  function handleCancel() {
    onClose(pending?.kind === 'prompt' ? null : false);
    reset();
  }

  function handleConfirm() {
    if (!canConfirm) return;
    if (pending?.kind === 'prompt') onClose(promptValue.trim());
    else onClose(true);
    reset();
  }

  return (
    <AnimatePresence>
      {pending && (
        <motion.div
          key={key}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 safe-pt safe-pb"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleCancel();
            else if (e.key === 'Enter' && canConfirm) handleConfirm();
          }}
        >
          <motion.button
            type="button"
            aria-label="Cancel"
            className="absolute inset-0 bg-ink/55 backdrop-blur-md"
            onClick={handleCancel}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />
          <motion.div
            initial={{ y: 12, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 8, opacity: 0, scale: 0.97 }}
            transition={SPRING_DEFAULT}
            className="relative w-full max-w-md overflow-hidden rounded-2xl border border-bone-deep bg-surface shadow-[0_30px_70px_-30px_rgba(10,10,10,0.4)]"
          >
            <button
              onClick={handleCancel}
              aria-label="Close"
              className="absolute right-3 top-3 rounded-full p-1 text-ink-muted transition-colors hover:bg-bone-warm hover:text-ink"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="px-6 pt-7">
              <div className="flex gap-3.5">
                {destructive && (
                  <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive/12">
                    <AlertTriangle className="h-4 w-4 text-destructive" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-medium leading-tight tracking-tightest">
                    {pending.title}
                  </h2>
                  {pending.description && (
                    <p className="mt-2 text-sm leading-relaxed text-ink-soft">
                      {pending.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Prompt input */}
              {pending.kind === 'prompt' && (
                <PromptField
                  pending={pending}
                  value={promptValue}
                  onValue={setPromptValue}
                  onSubmit={handleConfirm}
                />
              )}

              {/* Type-to-confirm */}
              {pending.kind === 'confirm' && typeRequired && (
                <div className="mt-5 space-y-1.5">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
                    Type <span className="text-ink">{typeRequired}</span> to confirm
                  </p>
                  <Input
                    autoFocus
                    value={typedToConfirm}
                    onChange={(e) => setTypedToConfirm(e.target.value)}
                    placeholder={typeRequired}
                    className="font-mono"
                  />
                </div>
              )}
            </div>

            <div
              className={cn(
                'mt-6 flex flex-row-reverse items-center gap-2 border-t border-bone-deep bg-bone-warm/40 px-6 py-4',
              )}
            >
              <Button
                variant={destructive ? 'destructive' : 'default'}
                size="sm"
                onClick={handleConfirm}
                disabled={!canConfirm}
                autoFocus={pending.kind === 'confirm' && !typeRequired}
              >
                {pending.confirmLabel ?? (pending.kind === 'prompt' ? 'Save' : 'Confirm')}
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                {pending.cancelLabel ?? 'Cancel'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Renders the prompt input — seeded with defaultValue on first paint. */
function PromptField({
  pending,
  value,
  onValue,
  onSubmit,
}: {
  pending: Extract<Pending, { kind: 'prompt' }>;
  value: string;
  onValue: (v: string) => void;
  onSubmit: () => void;
}) {
  // Seed the value from defaultValue once when this field mounts for the first time
  // for this dialog instance. Re-mounted automatically when key changes.
  if (value === '' && pending.defaultValue) {
    // schedule the initial value on next tick so React doesn't warn about
    // setState during render
    queueMicrotask(() => onValue(pending.defaultValue ?? ''));
  }

  return (
    <div className="mt-5 space-y-1.5">
      {pending.inputLabel && <Label>{pending.inputLabel}</Label>}
      <Input
        autoFocus
        value={value}
        onChange={(e) => onValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder={pending.placeholder}
        maxLength={pending.maxLength}
      />
    </div>
  );
}
